import { useRef } from "react";
import { toast } from "sonner";

import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useCreatePartnerMutation } from "Partners/Api/Mutations/useCreatePartnerMutation";
import {
  PartnerForm,
  type PartnerFormHandle,
  type PartnerPrefillData,
} from "Partners/Forms/PartnerForm";
import {
  type PartnerFormData,
  toSavePartnerRequest,
} from "Partners/Forms/partnerFormSchema";
import { BlModal } from "Shared/Dialogs/BlModal";
import type { BlOption } from "Shared/Inputs/BlOption";

interface CreatePartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  superiorPartnerOptions: BlOption[];
  parentPartnerID?: string;
  prefill?: PartnerPrefillData;
}

export const CreatePartnerModal = ({
  isOpen,
  onClose,
  superiorPartnerOptions,
  parentPartnerID,
  prefill,
}: CreatePartnerModalProps): React.ReactNode => {
  const { t } = useResource();
  const formRef = useRef<PartnerFormHandle | null>(null);
  const createPartnerMutation = useCreatePartnerMutation();

  const onSubmit = async (formData: PartnerFormData) => {
    try {
      await createPartnerMutation.mutateAsync(toSavePartnerRequest(formData));
      toast.success(t(Resources.Partners.CreatePartnerModal.SuccessMessage));
      formRef.current?.reset();
      onClose();
    } catch {
      // field errors shown in form, non-field errors as toast via useHandleServerError
    }
  };

  const handleClose = () => {
    formRef.current?.reset();
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Partners.CreatePartnerModal.Title)}
      onCancel={handleClose}
      onConfirm={() => formRef.current?.submit(onSubmit)()}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Partners.CreatePartnerModal.Create)}
      maxWidth="800px"
    >
      <PartnerForm
        partner={null}
        superiorPartnerOptions={superiorPartnerOptions}
        formRef={formRef}
        serverError={createPartnerMutation.error}
        lockedParentPartnerID={parentPartnerID}
        prefill={prefill}
      />
    </BlModal>
  );
};
